import React from "react";
import ReactDOM from 'react-dom';
import {AnimationSettingsProp, IStoreProductConfiguration, IStoreSettings} from "../../store/settings/types";
import {connect, DispatchProp} from "react-redux";
import {IApplicationState} from "../../store";
import {ListView, ListViewProps, ListViewState} from "../../shared/components/list-view/ListView";
import {memoize} from "../../shared/utils/memoize";
import listViewStyles from "../../shared/components/list-view/list-view.module.css";
import styles from "./store.module.css";
import renderText from "../../shared/components/renderText";
import {loadScene} from "../../store/routing/actions";
import {Animation, TimelineLite, TweenMax} from "gsap";
import {cssHelper} from "../../shared/utils/cssHelper";
import {purchase} from "../../store/payments/actions";
import {delay} from "redux-saga";
import topPanelStyles from "../../shared/components/top-panel/top-panel.module.css";
import icons from "../../shared/styles/icons.module.css";
import {iif} from "../../shared/components/iif";
import {BonusType} from "../../store/player/types";
import {trackError} from "../../store/log/actions";
import {LogLevel} from "../../store/log/types";
import {exportStateFlow} from "../../store/common/stateFlowEnhancer";
import {Overlay} from "../../shared/components/overlay/Overlay";
import {LoadingIndicator} from "../../shared/components/loading/LoadingIndicator";
import playSound from "../../shared/sounds/playSound";
import i18n from "../../shared/i18n";
import { IProduct } from "../../sdk/types";
import { SDK } from "../../sdk";

interface StoreSceneProps extends DispatchProp, AnimationSettingsProp, ListViewProps {
    readonly isReady: boolean;
    readonly purchaseInProgress: boolean;
    readonly consumeInProgress: boolean;
    readonly coins: number;
    readonly store: IStoreSettings;
}

type StoreItem = IProduct & IStoreProductConfiguration;

class StoreScene extends ListView<StoreItem, StoreSceneProps, ListViewState<StoreItem>> {
    private readonly activeTweens: Animation[] = [];

    constructor(props: StoreSceneProps, context: any) {
        super(props, context);

        this.loadPageInternal = memoize(this.loadPageInternal.bind(this));
    }

    render() {
        return (
            <section className={cssHelper(listViewStyles.container, styles.container).toString()}>
                <div className={topPanelStyles.top_panel}>
                    <button className={cssHelper(topPanelStyles.back_button, icons.back).toString()}
                            onClick={() => this.back()}>Back
                    </button>

                    {renderText(this.getHeader(), 32, 58, 15, 25, listViewStyles.header)}
                </div>

                {super.render()}

                <div className={styles.bottom_panel}>
                    <i className={cssHelper(icons.icon, icons.coins).toString()}/> {this.props.coins}
                </div>

                {iif(this.props.purchaseInProgress || this.props.consumeInProgress, (
                    <Overlay zIndex={10001}>
                        {iif(this.props.consumeInProgress, <LoadingIndicator />)}
                    </Overlay>
                ))}
            </section>
        );
    }

    componentDidMount(): void {
        this.props.dispatch(trackError({
            logLevel: LogLevel.Warn,
            category: "StoreScene",
            message: "Enter store",
            data: {
                state: exportStateFlow()
            }
        }));

        super.componentDidMount();

        const {animation} = this.props;

        const hostElement = ReactDOM.findDOMNode(this) as HTMLElement;

        if (!hostElement || !animation.enabled)
            return;

        const entries = hostElement.getElementsByClassName(listViewStyles.entry);

        const timeline = new TimelineLite()
            .staggerFrom(entries, 0.33, {
                y: 50,
                opacity: 0,
                delay: -0.025
            }, 0.125)
            .timeScale(animation.timeScale);

        this.activeTweens.push(timeline);
    }

    componentWillUnmount() {
        super.componentWillUnmount();

        for (let animation of this.activeTweens) {
            animation.kill();
        }
    }

    getHeader(): string {
        return i18n("inapppurchasesshop.defaultheader");
    }

    async loadPageInternal(page: number): Promise<{ entries: StoreItem[] }> {
        for (let i = 0; i < 5; i++) {
            if (this.props.isReady)
                break;

            await delay(1000);
        }

        const catalog = await SDK.instance.getInAppPurchasesCatalog();

        return {
            entries: this.props.store.products
                .filter(product => catalog.some(item => item.productId === product.productId))
                .map(product => ({
                    ...catalog.find(item => item.productId === product.productId)!,
                    ...product
                }))
        };
    }

    renderEntry(product: StoreItem): JSX.Element | undefined {
        const title = product.localizationKey
            ? i18n(product.localizationKey)
            : product.title;

        return (
            <div key={product.productId} className={cssHelper(listViewStyles.entry, listViewStyles.entries).toString()} onClick={(event) => this.purchase(event, product.productId)}>
                <div className={listViewStyles.picture} style={{ backgroundImage: `url('${product.image}')`}}/>
                <div className={listViewStyles.entry_data}>
                    {renderText(title, 28, 34, 24, 36, listViewStyles.entry_name)}
                    <div className={cssHelper(listViewStyles.entry_description, styles.entry_description).toString()}>
                        {iif(product.bonus === BonusType.Coins, <i className={cssHelper(icons.icon, icons.coins).toString()}/>, '×')}
                        {product.amount}
                    </div>
                </div>
                {renderText(product.price, 18, 26, 7, 12, styles.buy_button)}
            </div>
        );
    }

    renderEntries() {
        if (!this.state.entries || !this.state.entries.length)
            return;

        return (
            <>
                {this.state.entries.map(entry => this.renderEntry(entry))}
            </>
        );
    }

    back() {
        playSound("button-click");
        this.props.dispatch(loadScene.request("MainMenu"));
    }

    protected async trackError(message: string, error?: Error) {
        this.props.dispatch(trackError({
            category: "StoreScene",
            message: message,
            error: error,
        }));
    }

    private purchase(event: React.MouseEvent, productId: string) {
        const {animation, dispatch} = this.props;

        if (event && event.target && animation.enabled) {
            const tween = TweenMax.fromTo((event.target as HTMLElement).closest(`.${listViewStyles.entry}`)!, 0.05, {
                scale: 1,
            },{
                scale: 1.05,
                repeat: 1,
                yoyo: true,
                onComplete: () => dispatch(purchase.request(productId))
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);
        } else {
            dispatch(purchase.request(productId));
        }
    }
}

export default connect(({ payments, player, settings }: IApplicationState) => ({
    isReady: payments.isReady,
    purchaseInProgress: payments.purchaseInProgress,
    consumeInProgress: payments.consumeInProgress,
    coins: player.coins,
    store: settings.store,
    animation: settings.animation,
    pageSize: 100,
    scrollOffset: 100,

}))(StoreScene);